import { Controller } from "@hotwired/stimulus"

// Polls /admin/gridsignal/sources/full_ingest_status.json every 2s
// while a Full Ingest is running. Mirrors bulk_geocode_monitor_controller.
export default class GsFullIngestMonitorController extends Controller {
  static targets = [
    "statusBadge", "progressBar",
    "totalMeetings", "processedMeetings",
    "documentsAdded", "attachmentsAttempted", "attachmentsFailed",
    "meetingsSkipped",
    "elapsedText", "etaText", "currentStep",
    "errorBlock", "errorText"
  ]
  static values = { url: String, active: Boolean }

  connect() {
    if (this.activeValue) this.poll()
  }

  disconnect() {
    if (this._timer) clearTimeout(this._timer)
  }

  poll() {
    fetch(this.urlValue, { headers: { "Accept": "application/json" } })
      .then(r => r.json())
      .then(data => this.update(data))
      .catch(() => {})
      .finally(() => {
        if (this.activeValue) {
          this._timer = setTimeout(() => this.poll(), 2000)
        }
      })
  }

  update(data) {
    if (data.status === "none") return

    if (this.hasStatusBadgeTarget) {
      this.statusBadgeTarget.textContent = this.capitalize(data.status)
      this.statusBadgeTarget.className = `status-badge status-${data.status}`
    }

    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${data.progress_percentage}%`
    }

    if (this.hasTotalMeetingsTarget)       this.totalMeetingsTarget.textContent       = this.formatNumber(data.total_meetings)
    if (this.hasProcessedMeetingsTarget)   this.processedMeetingsTarget.textContent   = this.formatNumber(data.processed_meetings)
    if (this.hasDocumentsAddedTarget)      this.documentsAddedTarget.textContent      = this.formatNumber(data.documents_added)
    if (this.hasAttachmentsAttemptedTarget) this.attachmentsAttemptedTarget.textContent = this.formatNumber(data.attachments_attempted)
    if (this.hasAttachmentsFailedTarget)   this.attachmentsFailedTarget.textContent   = this.formatNumber(data.attachments_failed)
    if (this.hasMeetingsSkippedTarget)     this.meetingsSkippedTarget.textContent     = this.formatNumber(data.meetings_skipped_irrelevant)

    if (this.hasCurrentStepTarget && data.current_step) {
      this.currentStepTarget.textContent = data.current_step
    }

    if (this.hasElapsedTextTarget && data.elapsed_seconds != null) {
      this.elapsedTextTarget.textContent = this.formatDuration(data.elapsed_seconds)
    }

    if (this.hasEtaTextTarget) {
      if (data.status === "completed") {
        this.etaTextTarget.textContent = "Done"
      } else if (data.eta_seconds != null && data.eta_seconds > 0) {
        this.etaTextTarget.textContent = `~${this.formatDuration(data.eta_seconds)} remaining`
      } else {
        this.etaTextTarget.textContent = "Calculating…"
      }
    }

    if (data.status === "failed" && data.error_log) {
      if (this.hasErrorBlockTarget) this.errorBlockTarget.hidden = false
      if (this.hasErrorTextTarget)  this.errorTextTarget.textContent = data.error_log
    }

    if (data.status === "completed" || data.status === "failed") {
      this.activeValue = false
      // Only reload once every ingest card has settled. Reloading
      // while another parallel ingest is still running would reset
      // its live counts and look like it had stopped. We leave the
      // final state visible in-place and defer the reload until the
      // last card finishes — that way the chained ExtractionRun
      // card and settled failure-samples lists appear naturally.
      setTimeout(() => this.maybeReloadWhenAllDone(), 800)
    }
  }

  maybeReloadWhenAllDone() {
    const stillRunning = document.querySelectorAll(
      '[data-controller~="gs-full-ingest-monitor"][data-gs-full-ingest-monitor-active-value="true"]'
    )
    if (stillRunning.length === 0) {
      window.location.reload()
    }
  }

  capitalize(str) {
    if (!str) return ""
    return str.charAt(0).toUpperCase() + str.slice(1)
  }

  formatNumber(n) {
    if (n == null) return "0"
    return Number(n).toLocaleString()
  }

  formatDuration(totalSeconds) {
    if (totalSeconds == null) return "--"
    const h = Math.floor(totalSeconds / 3600)
    const m = Math.floor((totalSeconds % 3600) / 60)
    const s = Math.floor(totalSeconds % 60)
    if (h > 0) return `${h}h ${m}m ${s}s`
    if (m > 0) return `${m}m ${s}s`
    return `${s}s`
  }
}
